import { type FileId, FileType, fileId as fileIdSchema, type UploadedFile } from "@inrev/common";
import { useFileStatusDispatch } from "@portal/providers/FileStatusProvider";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { v4 } from "uuid";
import { getNameAndExtensionFromFileName } from "../../../utils";
import { FileClassificationSubscriber } from "../../files/FileClassificationSubscriber";
import { filesQueryKey } from "../../files/files.keys";
import { useFileUpload } from "./useFileUpload";
import { useSilentlyDeleteFile } from "./useSilentlyDeleteFile";

type FileClassificationState =
	| { status: "uploading" }
	| { status: "classifying" }
	| { status: "classified" }
	| { status: "uploadFailed" }
	| { status: "classificationFailed" };

type ClassificationFileState = {
	fileId: FileId;
	name: string;
	types: FileType[];
	uploadedFile?: UploadedFile;
	classification: FileClassificationState;
};

const EMPTY_FILES: ClassificationFileState[] = [];

export type UseFileClassificationPipelineOptions = {
	initialFiles?: UploadedFile[];
	onClassificationFailed?: (fileId: FileId) => void;
};

const classificationStateFromUploadedFile = (file: UploadedFile): FileClassificationState => {
	switch (file.classificationStatus) {
		case "completed":
			return { status: "classified" };
		case "failed":
			return { status: "classificationFailed" };
		default:
			// Both "pending" and "not_started" mean classification is not yet terminal — keep polling.
			return { status: "classifying" };
	}
};

const toInitialClassificationFileState = (file: UploadedFile): ClassificationFileState => ({
	fileId: file.id,
	name: file.name,
	types: file.types,
	uploadedFile: file,
	classification: classificationStateFromUploadedFile(file),
});

export const useFileClassificationPipeline = ({
	initialFiles,
	onClassificationFailed,
}: UseFileClassificationPipelineOptions) => {
	const queryClient = useQueryClient();
	const [files, setFiles] = useState<ClassificationFileState[]>(() =>
		initialFiles?.length ? initialFiles.map(toInitialClassificationFileState) : EMPTY_FILES,
	);

	const { silentlyDeleteFile } = useSilentlyDeleteFile();

	const { uploadFn } = useFileUpload({
		onUploadSuccess: (uploaded: UploadedFile[], { fileUploads }) => {
			uploaded.forEach((file, i) => {
				const clientId = fileUploads[i].pendingFile.id;
				queryClient.setQueryData(filesQueryKey(file.id), file);
				setFiles((prev) =>
					prev.map((f) => (f.fileId === clientId ? toInitialClassificationFileState(file) : f)),
				);
			});
		},
		onUploadError: (_error, { fileUploads }) => {
			const failedIds = new Set(fileUploads.map((u) => u.pendingFile.id));
			setFiles((prev) =>
				prev.map((f) =>
					failedIds.has(f.fileId) ? { ...f, classification: { status: "uploadFailed" } } : f,
				),
			);
		},
		suppressUploadErrorModal: true,
	});

	const handleFileUpdate = useCallback(
		(fileId: FileId, file: UploadedFile) => {
			setFiles((prev) =>
				prev.map((f) => {
					if (f.fileId !== fileId) return f;
					switch (file.classificationStatus) {
						case "completed":
							return {
								...f,
								uploadedFile: file,
								types: file.types,
								classification: { status: "classified" },
							};
						case "failed":
							return {
								...f,
								uploadedFile: file,
								classification: { status: "classificationFailed" },
							};
						default:
							// "pending" or "not_started" — update the stored file so the row can
							// derive the correct chrome from classificationStatus, but leave the
							// pipeline state as "classifying" so polling continues.
							return { ...f, uploadedFile: file };
					}
				}),
			);
			if (file.classificationStatus === "failed") {
				onClassificationFailed?.(fileId);
			}
		},
		[onClassificationFailed],
	);

	const handleDelete = useCallback(
		(fileId: FileId) => {
			setFiles((prev) => prev.filter((f) => f.fileId !== fileId));
			queryClient.removeQueries({ queryKey: filesQueryKey(fileId), exact: true });
			silentlyDeleteFile({ fileId });
		},
		[queryClient, silentlyDeleteFile],
	);

	const handleDrop = useCallback(
		(rawFiles: File[]) => {
			const fileUploads = rawFiles.map((rawFile) => {
				const { fileName, extension } = getNameAndExtensionFromFileName(rawFile.name);
				return {
					rawFile,
					pendingFile: { id: fileIdSchema.parse(v4()), name: fileName, extension, types: [] },
				};
			});
			setFiles((prev) => [
				...prev,
				...fileUploads.map((u) => ({
					fileId: u.pendingFile.id,
					name: u.pendingFile.name,
					types: [],
					classification: { status: "uploading" as const },
				})),
			]);
			uploadFn({ fileUploads });
		},
		[uploadFn],
	);

	const subscribers = files
		.filter((f) => f.classification.status === "classifying")
		.map((f) => (
			<FileClassificationSubscriber
				key={`classify-${f.fileId}`}
				fileId={f.fileId}
				onUpdate={handleFileUpdate}
			/>
		));

	const isAnyUploading = files.some((f) => f.classification.status === "uploading");

	const { setIsFileStatusPending } = useFileStatusDispatch();
	useEffect(() => {
		if (isAnyUploading) {
			setIsFileStatusPending(true);
			return () => setIsFileStatusPending(false);
		}
	}, [isAnyUploading, setIsFileStatusPending]);

	return {
		files,
		handleDrop,
		handleDelete,
		isAnyUploading,
		subscribers,
	};
};
